import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { defaultDragManager } from './DragManager.js';

export type DragAxis = 'x' | 'y' | 'both';

export interface UseDraggableOptions<T = unknown> {
  /** Semantic type — droppables filter by this. */
  type: string;
  /** Arbitrary payload the drop receives. */
  data: T;
  /** Preview node shown by `<DragOverlay>` during the drag. If omitted, the
   *  source element itself is rendered dimmed in place. */
  preview?: ReactNode;
  /** Minimum pointer travel before a drag actually begins (suppresses
   *  accidental drags on click). Default 4 px. */
  activationDistance?: number;
  /** Axis lock — drag movement is clamped to the selected axis. */
  axis?: DragAxis;
  /** Disable interaction entirely. */
  disabled?: boolean;
  /** Fires when the drag lifts off — after activationDistance is crossed. */
  onDragStart?: (data: T) => void;
  /** Fires after a successful drop (pointer released over a droppable). */
  onDragEnd?: (result: { over: string | null; data: T }) => void;
}

export interface UseDraggableResult {
  readonly setHandle: (el: HTMLElement | null) => void;
  readonly isDragging: boolean;
  readonly transform: { x: number; y: number };
}

/** Pointer-events-based draggable source. Attaches pointerdown /
 *  pointermove / pointerup to the handle element, observes pointer travel,
 *  and publishes drag state to the shared DragManager. Spec 13 §3.1. */
export function useDraggable<T = unknown>(options: UseDraggableOptions<T>): UseDraggableResult {
  const {
    type,
    data,
    preview,
    activationDistance = 4,
    axis = 'both',
    disabled = false,
    onDragStart,
    onDragEnd,
  } = options;

  const id = useId();
  const [isDragging, setDragging] = useState(false);
  const [transform, setTransform] = useState({ x: 0, y: 0 });

  // Latest options live in a ref so the pointerdown listener doesn't have to
  // tear down + reattach when callers re-render with a new payload mid-drag.
  // Re-attaching on isDragging would destroy the live pointermove/up
  // listeners we just registered — that's the bug that made drags impossible.
  const optsRef = useRef({
    type,
    data,
    preview,
    activationDistance,
    axis,
    onDragStart,
    onDragEnd,
  });
  optsRef.current = {
    type,
    data,
    preview,
    activationDistance,
    axis,
    onDragStart,
    onDragEnd,
  };

  const draggingRef = useRef(false);
  const startRef = useRef<{ x: number; y: number } | null>(null);

  const [handle, setHandleState] = useState<HTMLElement | null>(null);
  const setHandle = useCallback((el: HTMLElement | null): void => {
    setHandleState(el);
  }, []);

  useEffect(() => {
    if (handle === null || disabled) return undefined;

    const applyAxis = (dx: number, dy: number): { x: number; y: number } => {
      const a = optsRef.current.axis;
      if (a === 'x') return { x: dx, y: 0 };
      if (a === 'y') return { x: 0, y: dy };
      return { x: dx, y: dy };
    };

    const onPointerMove = (e: PointerEvent): void => {
      const start = startRef.current;
      if (start === null) return;
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      if (!draggingRef.current) {
        if (Math.hypot(dx, dy) < optsRef.current.activationDistance) return;
        draggingRef.current = true;
        setDragging(true);
        const o = optsRef.current;
        o.onDragStart?.(o.data);
        defaultDragManager.start({
          id,
          type: o.type,
          data: o.data,
          preview: o.preview ?? null,
          pointer: { x: e.clientX, y: e.clientY },
          delta: { x: 0, y: 0 },
          over: null,
        });
        return;
      }
      const t = applyAxis(dx, dy);
      setTransform(t);
      defaultDragManager.move({ x: e.clientX, y: e.clientY });
    };

    const onPointerUp = (): void => {
      const wasDragging = draggingRef.current;
      detachWindow();
      startRef.current = null;
      draggingRef.current = false;
      setTransform({ x: 0, y: 0 });
      setDragging(false);
      if (wasDragging) {
        const over = defaultDragManager.end();
        const o = optsRef.current;
        o.onDragEnd?.({ over, data: o.data });
      } else {
        defaultDragManager.cancel();
      }
    };

    const onPointerCancel = (): void => {
      detachWindow();
      startRef.current = null;
      draggingRef.current = false;
      setTransform({ x: 0, y: 0 });
      setDragging(false);
      defaultDragManager.cancel();
    };

    const detachWindow = (): void => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
    };

    const onPointerDown = (e: PointerEvent): void => {
      if (e.button !== 0) return;
      // Suppress the default text-selection/image-drag browser behaviour
      // that otherwise hijacks the pointer the moment we want to start
      // tracking it.
      e.preventDefault();
      startRef.current = { x: e.clientX, y: e.clientY };
      try {
        handle.setPointerCapture(e.pointerId);
      } catch {
        // Some elements (SVG without pointer-events, detached nodes) reject
        // setPointerCapture — fall back to plain window listeners.
      }
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
      window.addEventListener('pointercancel', onPointerCancel);
    };

    handle.addEventListener('pointerdown', onPointerDown);
    return () => {
      handle.removeEventListener('pointerdown', onPointerDown);
      detachWindow();
      startRef.current = null;
      draggingRef.current = false;
    };
  }, [handle, disabled, id]);

  return { setHandle, isDragging, transform };
}
